import { ACCESS_TIME_SLOT_MINUTES } from '@/utils/access/timeSlots';

type NamedEntity = {
	first_name?: string | null;
	last_name?: string | null;
	name?: string | null;
	email?: string | null;
};

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
/**
 * Preset expiries are aligned up to the next time slot. Ignore only that alignment when describing
 * the selected duration, otherwise seven days plus a few slot minutes reads as eight.
 */
const EXPIRY_SLOT_ALIGNMENT_MS = ACCESS_TIME_SLOT_MINUTES * MINUTE_MS;

/**
 * Composes a display name from first_name + last_name, falling back to the
 * legacy `name` field (SharingUser / UserMinimal) then email.
 */
export const getUserDisplayName = (user: NamedEntity | null): string => {
	if (!user) return '';
	const composed = [user.first_name, user.last_name].filter(Boolean).join(' ');
	if (composed) return composed;
	if (user.name) return user.name;
	return user.email ?? '';
};

/** Returns initials from a name or email */
export const getInitials = (name?: string | null, email?: string | null): string => {
	if (name) {
		// Punctuation carries no initial and must not be taken for one: "F&P" reads as "FP", and the
		// ampersand standing alone in "Fisher & Paykel" is dropped rather than counted as a word.
		const parts = name
			.trim()
			.split(/\s+/)
			.map((part) => part.replace(/[^\p{L}\p{N}]/gu, ''))
			.filter(Boolean);
		if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
		if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
	}
	if (email) return email.substring(0, 2).toUpperCase();
	return '??';
};

/** Formats expiry into a human-readable relative label, or 'Ongoing' for permanent access */
export const getExpiryLabel = (expiresAt: string | null | undefined): { label: string; expired: boolean } => {
	if (!expiresAt) return { label: 'Ongoing', expired: false };
	const now = new Date();
	const expiry = new Date(expiresAt);
	const diffMs = expiry.getTime() - now.getTime();
	if (diffMs < 0) return { label: 'Expired', expired: true };
	const diffMinutes = Math.ceil(diffMs / MINUTE_MS);
	if (diffMinutes < 60) return { label: `${diffMinutes} min`, expired: false };
	const alignedDiffMs = diffMs - EXPIRY_SLOT_ALIGNMENT_MS;
	const diffHours = Math.ceil(alignedDiffMs / HOUR_MS);
	const timezoneOffsetChangeMs = (expiry.getTimezoneOffset() - now.getTimezoneOffset()) * MINUTE_MS;
	const wallClockDiffMs = diffMs - timezoneOffsetChangeMs;
	if (diffHours < 24 && wallClockDiffMs < DAY_MS) return { label: `${diffHours} hr`, expired: false };
	const alignedWallClockDiffMs = wallClockDiffMs - EXPIRY_SLOT_ALIGNMENT_MS;
	const diffDays = Math.ceil(alignedWallClockDiffMs / DAY_MS);
	if (diffDays === 1) return { label: '1 day', expired: false };
	if (diffDays <= 31) return { label: `${diffDays} days`, expired: false };
	return { label: 'Ongoing', expired: false };
};
